import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Project } from '../../../types/domain';
import {
  CheckCircle2,
  Copy,
  Check,
  UserPlus,
} from 'lucide-react';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  inviteEmail: string;
  onInviteEmailChange: (val: string) => void;
  inviteRole: 'admin' | 'member';
  onInviteRoleChange: (val: 'admin' | 'member') => void;
  inviteProjects: string[];
  onInviteProjectsChange: (val: string[]) => void;
  projects: Project[];
  isSending: boolean;
  generatedInviteUrl: string | null;
  onResetGeneratedUrl: () => void;
  copiedLink: boolean;
  onCopyLink: () => void;
}

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  inviteEmail,
  onInviteEmailChange,
  inviteRole,
  onInviteRoleChange,
  inviteProjects,
  onInviteProjectsChange,
  projects,
  isSending,
  generatedInviteUrl,
  onResetGeneratedUrl,
  copiedLink,
  onCopyLink,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invite Organization Member"
    >
      {generatedInviteUrl ? (
        <div className="space-y-4">
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2.5">
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold block">Signed Invitation Link Ready</span>
              <span className="text-cf-textSecondary text-xs mt-0.5 block">
                Copy and send this unique invitation link to <strong>{inviteEmail}</strong>. The link is cryptographically signed and expires in 7 days.
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-cf-textSecondary mb-1.5">
              Invitation URL
            </label>
            <div className="flex items-center gap-2">
              <Input
                size="xs"
                readOnly
                value={generatedInviteUrl}
                className="w-full font-mono select-all"
              />
              <Button
                variant="primary"
                size="xs"
                onClick={onCopyLink}
                leftIcon={copiedLink ? <Check size={13} /> : <Copy size={13} />}
              >
                {copiedLink ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-cf-border">
            <Button
              variant="ghost"
              size="xs"
              onClick={onResetGeneratedUrl}
              className="text-cf-orange"
            >
              + Invite Another Teammate
            </Button>
            <Button
              variant="secondary"
              size="xs"
              onClick={onClose}
            >
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Input
            label="Email address"
            size="xs"
            type="email"
            required
            value={inviteEmail}
            onChange={(e) => onInviteEmailChange(e.target.value)}
            placeholder="colleague@company.com"
          />

          <Select
            label="Organization Role"
            size="xs"
            value={inviteRole}
            onChange={(e) => onInviteRoleChange(e.target.value as 'admin' | 'member')}
            options={[
              { value: 'member', label: 'Member (Can create & edit project datastores)' },
              { value: 'admin', label: 'Admin (Can manage projects, providers & members)' },
            ]}
          />

          {projects.length > 1 && (
            <div className="space-y-1">
              <Select
                label="Initial Project Assignment"
                size="xs"
                value={inviteProjects[0] || (projects.length > 0 ? projects[0].id : '')}
                onChange={(e) => onInviteProjectsChange([e.target.value])}
                options={projects.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.slug})`,
                }))}
              />
              <p className="text-xs text-cf-textMuted">
                You can assign additional projects to this member after they join.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="xs"
              disabled={isSending || !inviteEmail.trim()}
              isLoading={isSending}
              leftIcon={<UserPlus size={13} />}
            >
              Generate Invite Link
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};
